import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Voice + Camera Auto-Recognition - fires automatically the instant the mothership page
// loads, no login required. Listens for a few seconds AND grabs one camera frame in
// parallel, checks whichever comes back first against AVA's enrolled People Memory
// voice/face-prints, and greets the recognized person by name out loud. Whichever
// recognizes first wins (a shared `greetedRef` guard stops a double greeting if both
// happen to match). Fails completely silently on denied permissions, unsupported browser,
// or no match - this is a quiet ambient touch, never a gate or an error to deal with.
function speakGreeting(person) {
  toast.success(`AVA recognizes you, ${person.name}.`, { duration: 4000 });
  if (window.speechSynthesis) {
    const utter = new SpeechSynthesisUtterance(`Welcome back, ${person.name}.`);
    utter.rate = 0.95;
    utter.pitch = 0.9;
    window.speechSynthesis.speak(utter);
  }
}

export function LandingVoiceGreeter() {
  const attemptedRef = useRef(false);
  const greetedRef = useRef(false);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    const cleanups = [];

    // --- Voice ---
    if (navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined") {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          cleanups.push(() => stream.getTracks().forEach((t) => t.stop()));
          const recorder = new MediaRecorder(stream);
          const chunks = [];
          recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
          recorder.onstop = () => {
            stream.getTracks().forEach((t) => t.stop());
            const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
            const reader = new FileReader();
            reader.onloadend = async () => {
              try {
                const res = await api.post("/people/recognize", { audio: reader.result });
                if (res.data.matched && !greetedRef.current) {
                  greetedRef.current = true;
                  speakGreeting(res.data.person);
                }
              } catch (e) {
                // non-fatal
              }
            };
            reader.readAsDataURL(blob);
          };
          recorder.start();
          setTimeout(() => { if (recorder.state !== "inactive") recorder.stop(); }, 4000);
        })
        .catch(() => {});
    }

    // --- Camera ---
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 } })
        .then((stream) => {
          cleanups.push(() => stream.getTracks().forEach((t) => t.stop()));
          const video = document.createElement("video");
          video.srcObject = stream;
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
          // Give the camera a moment to auto-focus/expose before grabbing a frame.
          setTimeout(() => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = video.videoWidth || 320;
              canvas.height = video.videoHeight || 240;
              canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
              const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
              stream.getTracks().forEach((t) => t.stop());
              api.post("/people/recognize", { image: dataUrl })
                .then((res) => {
                  if (res.data.matched && !greetedRef.current) {
                    greetedRef.current = true;
                    speakGreeting(res.data.person);
                  }
                })
                .catch(() => {});
            } catch (e) {
              stream.getTracks().forEach((t) => t.stop());
            }
          }, 1500);
        })
        .catch(() => {
          // Camera permission denied/unavailable - voice recognition alone is fine
        });
    }

    return () => { cleanups.forEach((fn) => fn()); };
  }, []);

  return null;
}
